import { useState, useCallback } from "react";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

interface UseFetchProps {
  url: string;
  method?: HttpMethod;
}

const useFetch = ({ url, method = "GET" }: UseFetchProps) => {
  const [data, setData] = useState<any>([]);
  const [error, setError] = useState<ErrorType | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState<boolean>(false); // New flag for fetch status

  const fetchData = useCallback(async (body?: any) => {
    setIsLoading(true);
    setHasFetched(false); // Ensure this resets during new fetch
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No token found");
      }

      const options: RequestInit = {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      if (body && (method === "POST" || method === "PUT")) {
        options.body = JSON.stringify(body);
      }
      const response = await fetch(url, options);
      const result = await response.json();

      if (response.ok) {
        setData(result.data);
        return result;
      } else {
        setError(result || { code: response.status, message: "An error occurred" });
        return result;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
      setError({
        code: 500,
        message: errorMessage,
        success: false,
      });
    } finally {
      setIsLoading(false);
      setHasFetched(true); // Mark fetch as complete
    }
  }, [url, method]);

  type ErrorType = {
    code: number;
    message: string;
    success: boolean;
    data?: {
      message?: string;
      stack?: string;
    };
    error?: {
      error_message?: string;
    };
  };

  const errorContent = error?.message ? (
    <p>
      {error.code === 401 ? (
        <>
          ⚠️ Session expired. Please <a href="/login">login</a> again.
        </>
      ) : (
        <>{error.message}</>
      )}
    </p>
  ) : null;

  return { data, setData, error, errorContent, isLoading, fetchData, hasFetched };
};

export default useFetch;
